import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info, Volume2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import type { Notification, SystemAlertPayload } from '../../contexts/NotificationContext';
import type {
  InboxNotificationPriority,
  InboxNotificationType,
} from '../../services/inboxApi';

// Phase C.92: exhaustive lookup tables keyed by the inboxApi
// unions (mirror of Phase C.91 in NotificationCenter). Replaces
// switch ladders that each carried unreachable `default:`
// fallbacks and were yet another drift surface when the type
// catalogue grew. `Record<K, V>` makes a missing key a compile
// error and reuses the same React element across renders.
const TOAST_ICON: Record<InboxNotificationType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
  error: <AlertCircle className="w-5 h-5 text-red-500" />,
  info: <Info className="w-5 h-5 text-blue-500" />,
};

const TOAST_TYPE_STYLES: Record<InboxNotificationType, string> = {
  success: 'border-green-200 bg-green-50 text-green-900',
  warning: 'border-yellow-200 bg-yellow-50 text-yellow-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-blue-200 bg-blue-50 text-blue-900',
};

const TOAST_PRIORITY_STYLES: Record<InboxNotificationPriority, string> = {
  critical: 'ring-2 ring-red-500 ring-opacity-50',
  high: 'ring-1 ring-orange-400 ring-opacity-50',
  medium: '',
  low: 'opacity-90',
};

// SystemAlertBanner uses a strict subset of the notification type
// union (no 'success' — alerts are either info / warning / error).
type BannerType = SystemAlertPayload['type'];

const BANNER_ICON: Record<BannerType, React.ReactNode> = {
  warning: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
  error: <AlertCircle className="w-5 h-5 text-red-600" />,
  info: <Info className="w-5 h-5 text-blue-600" />,
};

const BANNER_STYLES: Record<BannerType, string> = {
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

interface ToastProps {
  notification: Notification;
  onClose: () => void;
  onAction?: (actionIndex: number) => void;
  className?: string;
}

const Toast: React.FC<ToastProps> = ({ notification, onClose, onAction, className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-hide timer (Phase C.76: 5s hardcoded — `duration` field
    // removed from UI Notification type, no caller ever overrode).
    if (notification.autoHide) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.autoHide]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match exit animation duration
  };

  const handleActionClick = (actionIndex: number, action: () => void) => {
    action();
    if (onAction) {
      onAction(actionIndex);
    }
    handleClose();
  };

  // Phase C.53: same smart navigation as `NotificationCenter`
  // (C.28) and `SystemAlertBanner` (C.35) — internal paths route
  // through react-router, external URLs open in a new tab. Toast
  // dismisses after the action so the user lands on their target
  // without the toast lingering.
  const followActionUrl = (url: string) => {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
    handleClose();
  };

  return (
    <div
      className={cn(
        'toast-container',
        'fixed z-50 max-w-sm w-full',
        'transform transition-all duration-300 ease-in-out',
        isVisible && !isExiting
          ? 'translate-x-0 opacity-100'
          : 'translate-x-full opacity-0',
        className
      )}
      role="alert"
      aria-live={notification.priority === 'critical' ? 'assertive' : 'polite'}
    >
      <div
        className={cn(
          'relative rounded-lg border shadow-lg p-4',
          'backdrop-blur-sm',
          TOAST_TYPE_STYLES[notification.type],
          TOAST_PRIORITY_STYLES[notification.priority]
        )}
      >
        {/* Priority indicator */}
        {notification.priority === 'critical' && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            {TOAST_ICON[notification.type]}
            <h4 className="font-semibold text-sm">{notification.title}</h4>
            {notification.priority === 'critical' && (
              <Volume2 className="w-4 h-4 text-red-500 animate-pulse" />
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Message */}
        <p className="text-sm mb-3 leading-relaxed">{notification.message}</p>

        {/* Metadata */}
        {notification.metadata && (
          <div className="text-xs opacity-75 mb-3">
            {notification.jobId && (
              <span className="inline-block bg-white bg-opacity-50 rounded px-2 py-1 mr-2">
                Job: {notification.jobId}
              </span>
            )}
            {notification.category && (
              <span className="inline-block bg-white bg-opacity-50 rounded px-2 py-1 mr-2 capitalize">
                {notification.category}
              </span>
            )}
          </div>
        )}

        {/* Phase C.53: actionUrl button — preferred path for
            server-pushed notifications (Phase C.28). */}
        {notification.actionUrl && (
          <div className="flex space-x-2 mt-3">
            <button
              onClick={() => followActionUrl(notification.actionUrl!)}
              className="px-3 py-1.5 text-xs font-medium rounded transition-colors bg-blue-600 text-white hover:bg-blue-700"
            >
              {notification.actionLabel ?? 'Open'}
            </button>
          </div>
        )}

        {/* Legacy closure-array actions (local toasts) */}
        {notification.actions && notification.actions.length > 0 && (
          <div className="flex space-x-2 mt-3">
            {notification.actions.map((action, index) => (
              <button
                key={index}
                onClick={() => handleActionClick(index, action.action)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded transition-colors',
                  action.variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700',
                  action.variant === 'secondary' && 'bg-gray-200 text-gray-800 hover:bg-gray-300',
                  action.variant === 'danger' && 'bg-red-600 text-white hover:bg-red-700',
                  !action.variant && 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Progress bar for auto-hide */}
        {notification.autoHide && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black bg-opacity-10 rounded-b-lg overflow-hidden">
            <div
              className="h-full bg-current opacity-30 transition-all ease-linear"
              style={{
                width: '100%',
                animation: `toast-progress 5000ms linear`,
              }}
            />
          </div>
        )}

        {/* Timestamp */}
        <div className="absolute top-2 right-8 text-xs opacity-50">
          {new Date(notification.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>

      <style jsx>{`
        @keyframes toast-progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  notifications: Notification[];
  onClose: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxToasts?: number;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  notifications,
  onClose,
  position = 'top-right',
  maxToasts = 5,
}) => {
  const getPositionStyles = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
      default:
        return 'top-4 right-4';
    }
  };

  // Filter notifications that should show as toasts
  const toastNotifications = notifications
    // Phase C.57: two distinct toast pathways:
    //   - Local autoHide toasts always show (Phase C.15 defaults
    //     them to read=true to skip bell flicker; toast shows
    //     independently and auto-dismisses after `duration`).
    //   - Server-pushed high/critical priority shows only while
    //     unread — Phase C.38 multi-tab sync can mark them read
    //     from a sibling tab, and the toast should disappear here
    //     to match.
    .filter(n =>
      n.autoHide ||
      (!n.read && (n.priority === 'critical' || n.priority === 'high'))
    )
    .slice(0, maxToasts);

  if (toastNotifications.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed z-50 pointer-events-none',
        getPositionStyles()
      )}
      aria-live="polite"
      aria-label="Notifications"
    >
      <div className="space-y-3 pointer-events-auto">
        {toastNotifications.map((notification, index) => (
          <Toast
            key={notification.id}
            notification={notification}
            onClose={() => onClose(notification.id)}
            className={cn(
              position.includes('left') ? 'toast-enter-left' : 'toast-enter-right'
            )}
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// System Alert Banner Component
// Phase C.81: shape imported from NotificationContext — single
// source of truth for the sticky banner payload.
interface SystemAlertBannerProps {
  alert: SystemAlertPayload;
  onDismiss: () => void;
}

export const SystemAlertBanner: React.FC<SystemAlertBannerProps> = ({ alert, onDismiss }) => {
  const navigate = useNavigate();
  // Phase C.35: route in-app for relative URLs, new tab for
  // absolute URLs — same rule as `NotificationCenter.followActionUrl`
  // (Phase C.28). Banner dismisses after navigation so the operator
  // doesn't see it twice once they're on the incident page.
  const followActionUrl = (url: string) => {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      onDismiss();
      navigate(url);
    }
  };
  return (
    <div
      className={cn(
        'border-l-4 p-4 mb-4 rounded-r-lg',
        BANNER_STYLES[alert.type]
      )}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {BANNER_ICON[alert.type]}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">{alert.title}</h3>
          <p className="mt-1 text-sm">{alert.message}</p>
        </div>
        <div className="ml-auto pl-3 flex items-center gap-2">
          {alert.actionUrl && (
            <button
              onClick={() => followActionUrl(alert.actionUrl!)}
              className="px-2 py-1 text-xs font-medium rounded border border-current hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
            >
              {alert.actionLabel ?? 'Open'}
            </button>
          )}
          {alert.dismissible && (
            <button
              onClick={onDismiss}
              className="inline-flex rounded-md p-1.5 hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
              aria-label="Dismiss alert"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toast;
